import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import * as log from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Archive, RotateCcw, Trash2, Hash, Lock } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import InvitePeopleDialog from "@/components/InvitePeopleDialog";
import InvitationsList from "@/components/InvitationsList";

const ROLES = ["owner", "admin", "manager", "member", "guest"];

export default function AdminView() {
  const { currentOrg, refreshMe } = useAuth();
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [org, setOrg] = useState(currentOrg);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitesReloadNonce, setInvitesReloadNonce] = useState(0);
  const [archived, setArchived] = useState([]);
  const [archivedBusy, setArchivedBusy] = useState({});

  const load = async () => {
    const [m, s] = await Promise.all([api.get("/orgs/current/members"), api.get("/orgs/current/stats")]);
    setMembers(m.data);
    setStats(s.data);
    try { const a = await api.get("/orgs/current/audit-logs"); setAudit(a.data); }
    catch (err) { log.debug("[admin] audit logs unavailable", err); }
    try { const arch = await api.get("/channels/archived"); setArchived(arch.data || []); }
    catch (err) { log.debug("[admin] archived channels unavailable", err); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setOrg(currentOrg); }, [currentOrg]);

  const changeRole = async (uid, role) => { await api.patch(`/orgs/current/members/${uid}/role`, null, { params: { role } }); toast.success("Role updated"); load(); };
  const remove = async (uid) => { if (!window.confirm("Remove member?")) return; await api.delete(`/orgs/current/members/${uid}`); toast.success("Removed"); load(); };

  const restoreChannel = async (id, name) => {
    setArchivedBusy((b) => ({ ...b, [id]: "restore" }));
    try {
      await api.post(`/channels/${id}/restore`);
      toast.success(`#${name} restored`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore");
    } finally {
      setArchivedBusy((b) => { const n = { ...b }; delete n[id]; return n; });
    }
  };

  const deleteArchived = async (id, name) => {
    const typed = window.prompt(`Type "${name}" to permanently delete this channel and every message inside it. This cannot be undone.`);
    if (typed !== name) { if (typed !== null) toast.error("Name mismatch"); return; }
    setArchivedBusy((b) => ({ ...b, [id]: "delete" }));
    try {
      await api.delete(`/channels/${id}`);
      toast.success(`#${name} deleted forever`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally {
      setArchivedBusy((b) => { const n = { ...b }; delete n[id]; return n; });
    }
  };

  const saveOrg = async (p) => { await api.patch("/orgs/current", p); toast.success("Saved"); await refreshMe(); };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="admin-view">
      <div className="h-14 border-b flex items-center px-6 shrink-0 gap-3">
        <h1 className="text-lg font-semibold">Admin</h1>
        {currentOrg?.internal_workspace && (
          <span
            data-testid="internal-workspace-badge"
            className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200"
            title="Only TOVRA platform admins can change this."
          >
            Internal TOVRA workspace — billing exempt
          </span>
        )}
        <Button
          size="sm"
          onClick={() => setInviteOpen(true)}
          data-testid="admin-invite-people-btn"
          className="ml-auto bg-[var(--brand-accent)] text-white hover:brightness-110"
        >
          <UserPlus className="w-4 h-4 mr-1.5" /> Invite people
        </Button>
      </div>
      <div className="flex-1 overflow-auto scroll-thin p-6 space-y-6 max-w-4xl">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard k="Members" v={stats.members}/>
            <StatCard k="Channels" v={stats.channels}/>
            <StatCard k="Messages" v={stats.messages}/>
            <StatCard k="Tasks" v={stats.tasks}/>
          </div>
        )}

        <section>
          <div className="text-xs uppercase text-zinc-500 mb-3">Organization</div>
          <div className="border rounded-lg p-4 space-y-3">
            <div>
              <label className="text-xs text-zinc-500">Name</label>
              <Input value={org?.name || ""} onChange={(e) => setOrg({ ...org, name: e.target.value })} onBlur={(e) => saveOrg({ name: e.target.value })}/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-zinc-500">Primary</label><Input type="color" value={org?.primary_color || "#000"} onChange={(e) => saveOrg({ primary_color: e.target.value })} className="h-11"/></div>
              <div><label className="text-xs text-zinc-500">Accent</label><Input type="color" value={org?.accent_color || "#D95D39"} onChange={(e) => saveOrg({ accent_color: e.target.value })} className="h-11"/></div>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase text-zinc-500">Members</div>
            <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)} data-testid="invite-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Invite
            </Button>
          </div>
          <div className="border rounded-lg divide-y">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-3 p-3">
                <Avatar user={m.user} size="lg"/>
                <div className="flex-1">
                  <div className="text-sm font-medium">{m.user?.name}</div>
                  <div className="text-xs text-zinc-500">{m.user?.email} · {m.department}</div>
                </div>
                <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                  <SelectTrigger className="w-32 h-8 text-xs" data-testid={`role-${m.user_id}`}><SelectValue/></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="ghost" onClick={() => remove(m.user_id)} data-testid={`remove-${m.user_id}`}>Remove</Button>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="text-xs uppercase text-zinc-500 mb-3">Pending invitations</div>
          <InvitationsList key={invitesReloadNonce} />
        </section>

        <section data-testid="archived-channels-section">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase text-zinc-500 flex items-center gap-1.5">
              <Archive className="w-3 h-3"/> Archived channels
              <span className="tabular-nums text-zinc-400">· {archived.length}</span>
            </div>
          </div>
          {archived.length === 0 ? (
            <div className="border border-dashed border-zinc-200 rounded-lg p-6 text-center">
              <div className="text-sm text-zinc-500">No archived channels.</div>
              <div className="text-xs text-zinc-400 mt-1">Channels sent to the archive from a channel&apos;s danger zone will show up here for restore or permanent delete.</div>
            </div>
          ) : (
            <div className="border rounded-lg divide-y">
              {archived.map((c) => (
                <div key={c.id} className="flex items-center gap-3 p-3" data-testid={`archived-row-${c.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-500 shrink-0">
                    {c.is_private ? <Lock className="w-4 h-4"/> : <Hash className="w-4 h-4"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      #{c.name}
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                        {c.is_private ? "private" : "public"}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 truncate">
                      {c.description || <span className="text-zinc-400">No description</span>}
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-0.5 flex flex-wrap gap-x-3">
                      <span>
                        Archived by <span className="text-zinc-600">{c.archived_by_user?.name || "—"}</span>
                        {c.archived_at && (
                          <span> · {new Date(c.archived_at).toLocaleString()}</span>
                        )}
                      </span>
                      <span>·</span>
                      <span>{c.message_count} messages in history</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!!archivedBusy[c.id]}
                    onClick={() => restoreChannel(c.id, c.name)}
                    data-testid={`archived-restore-${c.id}`}
                    className="gap-1.5"
                  >
                    <RotateCcw className="w-3.5 h-3.5"/>
                    {archivedBusy[c.id] === "restore" ? "Restoring…" : "Restore"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!!archivedBusy[c.id]}
                    onClick={() => deleteArchived(c.id, c.name)}
                    data-testid={`archived-delete-${c.id}`}
                    className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5"/>
                    {archivedBusy[c.id] === "delete" ? "Deleting…" : "Delete forever"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="text-xs uppercase text-zinc-500 mb-3">Audit log (recent)</div>
          <div className="border rounded-lg p-3 max-h-64 overflow-y-auto scroll-thin text-xs space-y-1">
            {audit.length === 0 && <div className="text-zinc-400">No audit entries yet.</div>}
            {audit.map((a) => <div key={a.id}>{new Date(a.created_at).toLocaleString()} · {a.actor} · {a.action}</div>)}
          </div>
        </section>
      </div>
      <InvitePeopleDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={() => setInvitesReloadNonce((n) => n + 1)}
      />
    </div>
  );
}

function StatCard({ k, v }) {
  return <div className="border rounded-lg p-4"><div className="text-xs uppercase text-zinc-500">{k}</div><div className="text-2xl font-semibold mt-1">{v}</div></div>;
}
